import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Eye, EyeOff, CheckCircle2, AlertCircle } from "lucide-react";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) setError("This reset link is missing or invalid. Request a new one.");
    }, [token]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 8) {
            setError("Password must be at least 8 characters");
            return;
        }
        if (password !== confirm) {
            setError("Passwords do not match");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/password-reset/confirm", { token, new_password: password });
            setDone(true);
            setTimeout(() => navigate("/login"), 2500);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "Reset failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-bone flex items-center justify-center px-4 py-12" data-testid="reset-password-page">
            <div className="w-full max-w-md">
                <div className="bg-white border border-ink/10 p-10">
                    <div className="overline">Account security</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-3xl text-ink leading-tight">
                        Set a new password
                    </h1>

                    {done ? (
                        <div className="mt-8 space-y-4" data-testid="reset-success">
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-green-50 text-green-700">
                                <CheckCircle2 className="w-6 h-6" />
                            </div>
                            <p className="text-sm text-ink/70">
                                Your password has been updated successfully. Redirecting you to sign in…
                            </p>
                            <Link to="/login" className="inline-block mt-2 text-sm text-klein hover:underline">
                                Sign in now →
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit}>
                            <p className="mt-3 text-sm text-ink/60 leading-relaxed">
                                Choose a strong password (at least 8 characters).
                            </p>
                            <div className="mt-8 space-y-6">
                                <div>
                                    <label className="overline">New password</label>
                                    <div className="relative mt-2">
                                        <input
                                            type={show ? "text" : "password"}
                                            required
                                            minLength={8}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full border-b border-ink/20 bg-transparent py-3 pr-10 px-0 focus:border-klein focus:outline-none text-ink"
                                            data-testid="reset-password-input"
                                        />
                                        <button type="button" onClick={() => setShow((s) => !s)}
                                            className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-ink/40 hover:text-ink"
                                            tabIndex={-1}>
                                            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="overline">Confirm password</label>
                                    <input
                                        type={show ? "text" : "password"}
                                        required
                                        minLength={8}
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        className="mt-2 w-full border-b border-ink/20 bg-transparent py-3 px-0 focus:border-klein focus:outline-none text-ink"
                                        data-testid="reset-confirm-input"
                                    />
                                </div>
                            </div>
                            {error && (
                                <div className="mt-6 border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2" data-testid="reset-error">
                                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={submitting || !token}
                                className="mt-8 w-full bg-klein text-white px-6 py-4 font-medium hover:bg-klein/90 disabled:opacity-60"
                                data-testid="reset-submit-button"
                            >
                                {submitting ? "Updating…" : "Update password"}
                            </button>
                            <Link to="/login" className="mt-6 block text-center text-xs uppercase tracking-[0.18em] text-ink/50 hover:text-klein">
                                Back to sign in
                            </Link>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
